/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {Location} from '@angular/common';
import {
    Subscription,
    timer
} from 'rxjs';
import {
    ActivatedRoute,
    Router
} from '@angular/router';
import {SpecialOrderDetailModel} from '../../api/ms-specialorders/contracts/special-order-detail.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {SpecialOrderService} from '../../api/ms-specialorders/specialorder/specialorder.service';
import {SpecialOrderBaseModel} from '../../api/ms-specialorders/contracts/special-order-base.model';
import {SpoHeaderDataModel} from '../models/spo-header-data.model';
import {SpoMaterialDataModel} from '../models/spo-material-data.model';
import {SpecialOrderMaterialDetailModel} from '../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {MaterialListService} from '../../shared/material-list/service/material-list.service';
import {SpecialOrderStatus} from '../../api/ms-specialorders/contracts/special-order-status.enum';
import {SpecialOrderCommandModel} from '../../api/ms-specialorders/contracts/special-order-command.model';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {MaterialValidateResultModel} from '../../api/ms-specialorders/contracts/material-validate-result.model';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {SpecialOrderMaterialBulkOperationCommandModel} from '../../api/ms-specialorders/contracts/special-order-material-bulk-operation-command-model';
import {SpecialOrderMaterialOperationCommandModel} from '../../api/ms-specialorders/contracts/special-order-material-operation-command-model';
import {SpecialOrderMaterialOperationEnum} from '../../api/ms-specialorders/contracts/special-order-material-operation.enum';
import {SpoActionsUtilService} from '../services/spo-actions-util/spo-actions-util.service';
import {CreateAndSubmitActionsEnum} from '../../api/generic/contracts/create-and-submit-actions.enum';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

/**
 * Internal state model, shouldn't be exported or accessed outside of the component.<br>
 * <b><i>If you're using it elsewhere, you're doing something wrong, very wrong.
 */
interface SpoEditPageStateModel {
    isOpen: boolean;
    detail: SpecialOrderDetailModel;
    materialList: SpecialOrderMaterialDetailModel[];
    originalMaterialList: SpecialOrderMaterialDetailModel[];
}

@Component({
    selector: 'app-spo-edit-page',
    templateUrl: './spo-edit-page.component.html',
    styleUrls: ['./spo-edit-page.component.css', '../../styles/base-edit-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoEditPageComponent implements OnInit, OnDestroy {
    /** SPO data variables*/
    public spoDetails: SpecialOrderDetailModel;
    public spoHeader: SpoHeaderDataModel;
    public spoMaterials: SpecialOrderMaterialDetailModel[];
    /** Variables for template buttons toggle */
    public isSpoOpen: boolean;
    public spoHeaderIsValidForSave = false;
    /** Array for materials to update */
    public materialsToUpdate: SpecialOrderMaterialOperationCommandModel[] = [];
    /** Invalid materials **/
    public invalidMaterials: string[] = [];
    /** Variable to manage form loading **/
    public isFormLoading = false;
    public isExited = false;
    public maxNumberOfMaterials: string = null;
    /*
     * TODO : Create a shared file w/ const values across the application
     */
    public MODAL_TITLE = 'SPO edition';
    private materialListHasErrors = false;
    /** Subscriptions variable */
    private subscriptions = new Subscription();
    /** Width of the confirmation modal*/
    private confirmModalWidth = 500;
    private state: SpoEditPageStateModel = {
        isOpen: false,
        detail: null,
        materialList: null,
        originalMaterialList: null
    };

    constructor(
            private activatedRoute: ActivatedRoute,
            private router: Router,
            private toast: ToastWrapperService,
            private specialOrderService: SpecialOrderService,
            private location: Location,
            private modalService: NgbModal,
            private materialListService: MaterialListService,
            private loadingService: LoadingService,
            private unsavedChangesService: UnsavedChangesService,
            private replay: ReplayableRouterEventsService,
            private spoActionsUtilService: SpoActionsUtilService
    ) {
        /** Start spinner loading*/
        this.loadingService.start();
    }

    ngOnInit(): void {

        /** Sets subscriptions for the material list service **/
        this.getMaxMaterialsConfiguration();
        this.setMaterialListDataSubscription();
        this.setMaterialInvalidNumbersSubscription();
        this.setMaterialListHasErrors();

        /** Gets SPO data from the activated route*/
        this.subscriptions.add(
                this.activatedRoute.data.subscribe(
                        (data) => {
                            this.spoDetails = data.spoDetail;
                            this.isSpoOpen = this.spoDetails.status.code === SpecialOrderStatus.OPEN;

                            this.spoHeader = {...this.spoDetails, requiresTransportService: false};

                            this.spoMaterials = this.spoDetails.specialOrderMaterials ?
                                    this.spoDetails.specialOrderMaterials : [];

                            this.state = {
                                ...this.state,
                                isOpen: data.spoDetail.status.code === SpecialOrderStatus.OPEN,
                                detail: data.spoDetail,
                                materialList: data.spoDetail.specialOrderMaterials != null ?
                                        data.spoDetail.specialOrderMaterials : [],
                                originalMaterialList: data.spoDetail.specialOrderMaterials != null ?
                                        data.spoDetail.specialOrderMaterials : []
                            };

                            this.unsavedChangesService.originalValue = this.state;

                            /** Refreshes the material list service */
                            this.refreshInitialMaterialInvalidNumbers();
                            this.materialListService.refreshMaterialData(this.spoMaterials);

                        },
                        (error) => this.errorModal('The information that you are looking for is not available right now.',
                                'Error', () => this.router.navigate(['/', 'spo'])
                        ))
        );
    }

    ngOnDestroy(): void {
        /** Unsubscribe to all subscriptions */
        this.subscriptions.unsubscribe();
        this.unsavedChangesService.originalValue = undefined;
    }

    refreshInitialMaterialInvalidNumbers(): void {
        const materialNumbers: string[] = [];
        this.spoMaterials.forEach((material) => materialNumbers.push(material.material.number));
        this.materialListService.refreshMaterialNumbers(materialNumbers);
    }

    /**
     * Updates the SPO header values and checks if it's valid to save
     */
    updateSpoHeaderValues(formValues: SpoHeaderDataModel): void {
        this.spoHeader = {
            partner: formValues.partner,
            shipToParty: formValues.shipToParty,
            supplyingPackingPlant: formValues.supplyingPackingPlant,
            reason: formValues.reason ? formValues.reason : null,
            requestedDate: formValues.requestedDate ? formValues.requestedDate : null,
            transportMode: formValues.transportMode ? formValues.transportMode : null,
            transportService: formValues.transportService ? formValues.transportService : null,
            justification: formValues.justification ? formValues.justification : null,
            requiresTransportService: formValues.requiresTransportService
        };

        this.state = {
            ...this.state,
            detail: {
                ...this.state.detail,
                partner: formValues.partner,
                shipToParty: formValues.shipToParty,
                supplyingPackingPlant: formValues.supplyingPackingPlant
            }
        };

        if (formValues.reason != null) {
            this.state.detail = {
                ...this.state.detail,
                reason: formValues.reason
            };
        }

        // Ignoring requested date on purpose, it's affecting the guard even if the user doesn't change the value.
        // This keeps the previous behaviour, where we were deleting the requested date from the current state.

        if (formValues.transportMode != null) {
            this.state.detail = {
                ...this.state.detail,
                transportMode: formValues.transportMode
            };
        }

        if (formValues.transportService != null) {
            this.state.detail = {
                ...this.state.detail,
                transportService: formValues.transportService
            };
        }

        if (formValues.justification != null) {
            this.state.detail = {
                ...this.state.detail,
                justification: formValues.justification
            };
        }

        this.unsavedChangesService.currentValue = this.state;

        this.spoHeaderIsValidForSave = formValues.partner != null && formValues.partner.code != null
                && formValues.shipToParty != null && formValues.shipToParty.code != null
                && formValues.supplyingPackingPlant != null && formValues.supplyingPackingPlant.code != null;
    }

    /**
     * Action to the cancel click
     * Sends user to detail page or the previous visited location
     */
    cancelClick(): void {
        if (this.isSpoOpen) {
            this.router.navigate(['/', 'spo', this.spoDetails.number]);
        } else {
            this.router.navigate(['/', 'spo']);
        }
    }

    /**
     * Action to the save button click
     * SPO is open: shows confirmation modal and on confirm event calls {@link updateMaterials}
     * SPO is Draft: calls {@link updateSpo}
     */
    saveClick(): void {
        if (this.isSpoOpen) {
            const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
                centered: true
            }).componentInstance;
            confirmModalInstance.initModal({
                titleText: 'Confirm',
                subtitleText: 'Do you really want to save changes?',
                alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
                primaryButtonText: 'Save',
                secondaryButtonText: 'Cancel',
                width: this.confirmModalWidth,
                manualChecks: {
                    title: `<strong>Be aware that</strong>`,
                    inputs: [
                        {
                            checked: false,
                            message: 'This action will send a cancellation request for each cancelled material(s).'
                        },
                        {
                            checked: false,
                            message: 'The cancelled material(s) will no longer be considered in this order.'
                        }
                    ]
                }
            } as ConfirmModal)
                    .confirmEvent
                    .subscribe(() => {
                        this.updateMaterials();
                    });
        } else {
            this.updateSpo();
            this.isExited = true;
        }
    }

    /**
     * Adds the selected material to the materials to cancel list
     * Enables the send button on a Open SPO
     * @param event Material to update
     */
    addMaterialToUpdate(event: SpecialOrderMaterialOperationCommandModel): void {
        this.materialsToUpdate.push(event);

        this.state = {
            ...this.state,
            materialList: this.state.originalMaterialList
                    .filter((material) => !this.materialsToUpdate.some((update) => update.position === material.position))
        };

        this.unsavedChangesService.currentValue = this.state;
        this.materialListService.refreshMaterialData(this.spoMaterials);
    }

    /**
     * Undo material to update
     */
    undoMaterialToUpdate(materialToCancel: SpecialOrderMaterialDetailModel): void {
        this.materialsToUpdate = this.materialsToUpdate.filter((material) => material.position !== materialToCancel.position);

        this.state = {
            ...this.state,
            materialList: this.state.originalMaterialList
                    .filter((material) => !this.materialsToUpdate.some((update) => update.position === material.position))
        };

        this.unsavedChangesService.currentValue = this.state;
    }

    /**
     * Updates Draft SPO and sends to the BE
     */
    updateSpo(): void {
        /** Start spinner loading*/
        this.loadingService.start();
        /** Updates the SPO*/
        const subscription = this.specialOrderService.updateSpo(this.spoDetails.number, this.createUpdatedSpo())
                .subscribe(
                        (data: SpecialOrderBaseModel) => {
                            this.unsavedChangesService.ignoreChanges();
                            this.router.navigate(['/', 'spo', data.number]);
                            this.toast.success(
                                    `SPO No. <span class="toast-edited-spo-number">${data.number} 
</span><strong>updated</strong> successfully!`);
                        }, () => {
                            this.errorModal(`Something went wrong while saving SPO's changes,`, 'Error');
                        }
                )
                .add(() => {
                    /** Stop spinner loading*/
                    this.loadingService.stop();
                });
        this.subscriptions.add(subscription);
    }

    /** Filter invalid materials **/
    filterInvalidMaterials(listMaterials: SpecialOrderMaterialDetailModel[]): SpecialOrderMaterialDetailModel[] {
        return listMaterials.filter((material) => !this.invalidMaterials.includes(material.material.number));
    }

    /** Filter deleted materials **/
    filterDeletedMaterials(listMaterials: SpecialOrderMaterialDetailModel[]): SpecialOrderMaterialDetailModel[] {
        return this.materialsToUpdate.length > 0 ?
                listMaterials.filter((material) => {
                    const position = this.materialsToUpdate.find((m) => m.position === material.position);
                    return position == null || position.action !== SpecialOrderMaterialOperationEnum.DELETE;
                }) : listMaterials;
    }

    /** Creates the updated SPO*/
    createUpdatedSpo(): SpecialOrderCommandModel {
        return {
            partnerCode: null,
            shipToPartyCode: null,
            supplyingPackingPlantCode: this.spoHeader.supplyingPackingPlant.code,
            reasonCode: this.spoHeader.reason ? this.spoHeader.reason.code : null,
            requestedDate: this.spoHeader.requestedDate,
            transportModeCode: this.spoHeader.transportMode ? this.spoHeader.transportMode.code : null,
            transportServiceCode: this.spoHeader.transportService ? this.spoHeader.transportService.code : null,
            justification: this.spoHeader.justification && this.spoHeader.justification.length > 0
                    ? toBase64Utf16LittleEndian(this.spoHeader.justification)
                    : null,
            specialOrderMaterials: this.spoMaterials ?
                    this.filterDeletedMaterials(this.filterInvalidMaterials(this.spoMaterials))
                            .map((material: SpoMaterialDataModel) => ({
                                number: material.material.number,
                                lotNumber: material.lotNumber,
                                mcdrNumber: material.mcdrNumber,
                                igefNumber: material.igefNumber,
                                colourCode: material.colourCode,
                                modelType: material.modelType,
                                orderedQuantity: material.orderedQuantity
                            })) : []
        };
    }

    /**
     * Updates materials from Open SPO
     */
    updateMaterials(): void {
        this.sendMaterialsToUpdate({positions: this.materialsToUpdate});
    }

    /**
     * Sends materials to update to the BE
     * @param positions The positions of the materials to update
     * */
    sendMaterialsToUpdate(positions: SpecialOrderMaterialBulkOperationCommandModel): void {
        /** Start spinner loading*/
        this.loadingService.start();
        this.subscriptions.add(
                this.specialOrderService.updateSpecialOrderMaterials(this.spoDetails.number, positions)
                        .subscribe((response) => {
                                    const materialsToUpdate = this.materialsToUpdate.length;
                                    const number = this.spoDetails.number;
                                    this.unsavedChangesService.ignoreChanges();
                                    if (response.status === 207) {
                                        const multiStatus = response.body;
                                        const submitAction = multiStatus.actions
                                                .filter((status) => status.action
                                                        !== CreateAndSubmitActionsEnum.CREATE && status.error == null);
                                        const successUpdatedMaterial = submitAction.length;
                                        const materialUpdateErrorMessage = multiStatus.actions.find((s) => s.error != null).error.message;
                                        this.toast.error(this.spoActionsUtilService.getMultiStatusToastErrorMessage(successUpdatedMaterial,
                                                materialsToUpdate, number, materialUpdateErrorMessage, 'updated'), 'Error');
                                    } else {
                                        if (this.materialsToUpdate.length === 1) {
                                            const materialNumber = this.spoDetails.specialOrderMaterials
                                                    .find((m) => m.position === this.materialsToUpdate[0].position).material.number;
                                            const actionMessage = this.getActionLabel(this.materialsToUpdate[0].action);
                                            this.toast.success(
                                                    `Material Nº ${materialNumber} was <strong>${actionMessage}</strong> successfully!`);
                                        } else {
                                            this.toast
                                                    .success(`${materialsToUpdate} materials were <strong>updated</strong> successfully!`);
                                        }
                                    }
                                    this.router.navigate(['/', 'spo', number]);
                                }, () => {
                                    this.errorModal(`Something went wrong while saving SPO's changes,`, 'Error');
                                }
                        )
                        .add(() => {
                            /** Stop spinner loading*/
                            this.loadingService.stop();
                        }));
    }

    /**
     * Returns the label according to the action
     * @param action the SpecialOrderMaterialOperationEnum
     */
    getActionLabel(action: SpecialOrderMaterialOperationEnum): string {
        switch (action) {
            case SpecialOrderMaterialOperationEnum.CANCEL:
                return 'cancelled';
            case SpecialOrderMaterialOperationEnum.DELETE:
                return 'deleted';
            default:
                return 'updated';
        }
    }

    isDisabled(): boolean {
        return this.spoDetails.status.code === SpecialOrderStatus.OPEN && this.materialsToUpdate.length === 0
                || !this.spoHeaderIsValidForSave
                || this.materialListHasErrors;
    }

    manageFormLoading = (formIsLoading: boolean): void => {
        timer(0)
                .subscribe(() => {
                    this.isFormLoading = formIsLoading;
                });
    };

    /** Creates an error modal */
    private errorModal(subtitle: string, title: string, callback?: Function): void {
        const errorModalInstance: ConfirmModalComponent = this.modalService
                .open(ConfirmModalComponent, {centered: true}).componentInstance;
        errorModalInstance.initModal({
            titleText: title,
            subtitleText: subtitle,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            primaryButtonText: null,
            secondaryButtonText: 'Close'
        } as ConfirmModal);
        if (typeof callback === 'function') {
            callback();
        }
    }

    private getMaxMaterialsConfiguration(): void {
        this.subscriptions.add(
                this.specialOrderService.getSpecialOrderConfig()
                        .subscribe((maxMaterialsConfig) =>
                                this.maxNumberOfMaterials = maxMaterialsConfig.maxNumberOfMaterials));
    }

    private setMaterialListDataSubscription(): void {
        this.subscriptions.add(
                this.materialListService.materialData.subscribe((materials) => {
                    this.spoMaterials = materials;

                    this.state = {
                        ...this.state,
                        materialList: materials
                                .filter((material) => !this.materialsToUpdate.some((update) => update.position === material.position))
                    };

                    this.unsavedChangesService.currentValue = this.state;
                }));
    }

    private setMaterialInvalidNumbersSubscription(): void {
        this.subscriptions.add(
                this.materialListService.invalidMaterials.subscribe((invalidMaterials: MaterialValidateResultModel) =>
                        this.invalidMaterials = invalidMaterials.materialNumbers));
    }

    private setMaterialListHasErrors(): void {
        this.subscriptions.add(
                this.materialListService.materialListHasErrors.subscribe((errors) => this.materialListHasErrors = errors));
    }
}
